import React, { useMemo, useState } from 'react';
import {
  CASH_STAGES,
  BANK_STAGES,
  stagesForPayment,
  parseChecklist,
  completedCount,
  isDealClosed,
  projLabel,
  sortProjects,
  fmt,
} from './constants';

// ── ClosureDashboard ──
// Project-grouped "command center" view of every reserved/sold/transferred
// unit. Surfaces, per project: in-flight count, SAR blocked, top blocker
// step, average + oldest age, and a drill-in table with stuck-step / age /
// value / open-deal action. Projects that are fully closed collapse to a
// one-line summary so the page stays scannable.
export default function ClosureDashboard({ units, deals = [], lang, t, onEditDeal, onMarkSold }) {
  const [statusFilter, setStatusFilter] = useState('all'); // all | inflight | closed — default 'all' so Closure matches the Deals tab count
  const [paymentFilter, setPaymentFilter] = useState('all'); // all | كاش | بنك
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('blocker'); // blocker | value | name
  const [stepFilter, setStepFilter] = useState(null); // { payment, idx } — set by clicking a bottleneck row
  const [expanded, setExpanded] = useState({}); // { [project]: bool }

  const today = useMemo(() => Date.now(), []);

  // Build deal lookup once: project|unit_code → deal row (for price + dates).
  const dealByUnit = useMemo(() => {
    const m = new Map();
    deals.forEach((d) => m.set(`${d.project}|${d.unit_code}`, d));
    return m;
  }, [deals]);

  // Only reserved/sold/transferred units that ALSO have a registered deal
  // belong in the closure pipeline. Seeded/historical "sold" units with no
  // deal row (e.g. yamamFlowers12/H01) were showing here but not in the Deals
  // tab — so the two tabs now read from the same set of deals.
  const closureUnits = useMemo(
    () => units.filter(
      (u) =>
        (u.status === 'محجوز' || u.status === 'مباع' || u.status === 'أفرغ') &&
        dealByUnit.has(`${u.project}|${u.unit_code}`)
    ),
    [units, dealByUnit]
  );

  // Enrich each unit: stages, checklist progress, stuck-step, age, value.
  const enriched = useMemo(() => {
    return closureUnits.map((u) => {
      const stages = stagesForPayment(u.payment_method);
      const checklist = parseChecklist(u.deal_checklist);
      const done = completedCount(stages, checklist);
      const closed = isDealClosed(stages, checklist);
      // Stuck step = first stage where the answer doesn't satisfy completion.
      let stuckIdx = -1;
      for (let i = 0; i < stages.length; i++) {
        const s = stages[i];
        const v = checklist[i];
        const ok = s.optional ? (v === 'yes' || v === 'no') : v === 'yes';
        if (!ok) { stuckIdx = i; break; }
      }
      const deal = dealByUnit.get(`${u.project}|${u.unit_code}`) || null;
      const value = Number((deal && (deal.actual_price || deal.price)) || u.price || 0);
      // Age = days since unit was last touched (checklist edits bump updated_at).
      const ts = u.updated_at ? new Date(u.updated_at).getTime() : (u.created_at ? new Date(u.created_at).getTime() : today);
      const ageDays = Math.max(0, Math.floor((today - ts) / (1000 * 60 * 60 * 24)));
      return {
        ...u,
        _stages: stages,
        _checklist: checklist,
        _done: done,
        _closed: closed,
        _stuckIdx: stuckIdx,
        _stuckLabel: stuckIdx >= 0 ? stages[stuckIdx][lang] : '',
        _value: value,
        _ageDays: ageDays,
        _deal: deal,
      };
    });
  }, [closureUnits, dealByUnit, lang, today]);

  // Global filters (status, payment, search, stuck-step click-through).
  const visible = useMemo(() => {
    let list = enriched;
    if (statusFilter === 'inflight') list = list.filter((u) => !u._closed);
    else if (statusFilter === 'closed') list = list.filter((u) => u._closed);
    if (paymentFilter !== 'all') list = list.filter((u) => u.payment_method === paymentFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (u) =>
          (u.unit_code || '').toLowerCase().includes(q) ||
          (u.client_name || '').toLowerCase().includes(q) ||
          (u.closer || '').toLowerCase().includes(q)
      );
    }
    if (stepFilter) {
      list = list.filter(
        (u) =>
          !u._closed &&
          u.payment_method === stepFilter.payment &&
          u._stuckIdx === stepFilter.idx
      );
    }
    return list;
  }, [enriched, statusFilter, paymentFilter, search, stepFilter]);

  // Top-level KPIs (over the visible set).
  const kpis = useMemo(() => {
    const total = visible.length;
    const closed = visible.filter((u) => u._closed).length;
    const inflight = total - closed;
    const sarInflight = visible.filter((u) => !u._closed).reduce((a, u) => a + u._value, 0);
    const sarClosed = visible.filter((u) => u._closed).reduce((a, u) => a + u._value, 0);
    const ages = visible.filter((u) => !u._closed).map((u) => u._ageDays);
    const avgAge = ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
    const oldest = ages.length ? Math.max(...ages) : 0;
    const pct = total > 0 ? Math.round((closed / total) * 100) : 0;
    return { total, closed, inflight, sarInflight, sarClosed, avgAge, oldest, pct };
  }, [visible]);

  // Bottleneck per pipeline — computed over the visible (filtered) set so it
  // tracks whatever the user is looking at.
  const bottleneck = useMemo(() => {
    const buildFor = (stages, paymentMatch) => {
      const counts = stages.map(() => 0);
      visible
        .filter((u) => !u._closed && u.payment_method === paymentMatch)
        .forEach((u) => {
          stages.forEach((s, i) => {
            const v = u._checklist[i];
            const answered = s.optional ? (v === 'yes' || v === 'no') : v === 'yes';
            if (!answered) counts[i]++;
          });
        });
      return stages.map((s, i) => ({ idx: i, label: s[lang], count: counts[i], optional: !!s.optional }));
    };
    return {
      cash: buildFor(CASH_STAGES, 'كاش'),
      bank: buildFor(BANK_STAGES, 'بنك'),
    };
  }, [visible, lang]);

  // Group visible units by project for the card grid.
  const groups = useMemo(() => {
    const map = new Map();
    visible.forEach((u) => {
      if (!map.has(u.project)) map.set(u.project, []);
      map.get(u.project).push(u);
    });
    let arr = Array.from(map.entries()).map(([project, list]) => {
      const inflight = list.filter((u) => !u._closed);
      const closed = list.filter((u) => u._closed);
      const sarInflight = inflight.reduce((a, u) => a + u._value, 0);
      const sarClosed = closed.reduce((a, u) => a + u._value, 0);
      const ages = inflight.map((u) => u._ageDays);
      const avgAge = ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
      const oldestUnit = inflight.length
        ? inflight.reduce((max, u) => (u._ageDays > max._ageDays ? u : max), inflight[0])
        : null;
      // Top blocker = step index (per pipeline) with the most stuck units.
      const blockerCount = {};
      inflight.forEach((u) => {
        if (u._stuckIdx < 0) return;
        const key = `${u.payment_method}|${u._stuckIdx}`;
        blockerCount[key] = (blockerCount[key] || 0) + 1;
      });
      let topBlocker = null;
      let topCount = 0;
      Object.entries(blockerCount).forEach(([key, n]) => {
        if (n > topCount) {
          topCount = n;
          const [payment, idx] = key.split('|');
          const stages = stagesForPayment(payment);
          topBlocker = { payment, idx: Number(idx), label: stages[Number(idx)][lang], count: n };
        }
      });
      const cashN = list.filter((u) => u.payment_method === 'كاش').length;
      const bankN = list.filter((u) => u.payment_method === 'بنك').length;
      const closureRate = list.length > 0 ? Math.round((closed.length / list.length) * 100) : 0;
      return {
        project,
        list,
        inflight,
        closed,
        sarInflight,
        sarClosed,
        avgAge,
        oldestUnit,
        topBlocker,
        cashN,
        bankN,
        closureRate,
        allClosed: inflight.length === 0 && closed.length > 0,
      };
    });
    if (sortKey === 'value') {
      arr.sort((a, b) => b.sarInflight - a.sarInflight);
    } else if (sortKey === 'name') {
      const ordered = sortProjects(arr.map((g) => g.project));
      arr.sort((a, b) => ordered.indexOf(a.project) - ordered.indexOf(b.project));
    } else {
      // blocker (default): (inflight × avgAge) desc, then SAR desc.
      arr.sort((a, b) => {
        const sa = a.inflight.length * Math.max(1, a.avgAge);
        const sb = b.inflight.length * Math.max(1, b.avgAge);
        if (sb !== sa) return sb - sa;
        return b.sarInflight - a.sarInflight;
      });
    }
    return arr;
  }, [visible, sortKey, lang]);

  const totalKpis = useMemo(() => {
    // Always reflect the FULL enriched set on the top strip, not the
    // step-filtered subset, so the executive numbers stay stable as the
    // user drills in.
    const total = enriched.length;
    const closed = enriched.filter((u) => u._closed).length;
    const inflight = total - closed;
    const sarInflight = enriched.filter((u) => !u._closed).reduce((a, u) => a + u._value, 0);
    const sarClosed = enriched.filter((u) => u._closed).reduce((a, u) => a + u._value, 0);
    const ages = enriched.filter((u) => !u._closed).map((u) => u._ageDays);
    const avgAge = ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
    const pct = total > 0 ? Math.round((closed / total) * 100) : 0;
    return { total, closed, inflight, sarInflight, sarClosed, avgAge, pct };
  }, [enriched]);

  const toggleExpand = (project) =>
    setExpanded((prev) => ({ ...prev, [project]: !prev[project] }));

  const clearStepFilter = () => setStepFilter(null);

  const isAr = lang === 'ar';

  return (
    <div>
      {/* ── Executive KPI strip ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <Kpi
          label={isAr ? 'إجمالي الصفقات' : 'Total Deals'}
          value={totalKpis.total}
          accent="var(--accent)"
        />
        <Kpi
          label={isAr ? 'قيد التنفيذ' : 'In Progress'}
          value={totalKpis.inflight}
          sub={`${fmt(totalKpis.sarInflight)} SAR`}
          accent="#f59e0b"
        />
        <Kpi
          label={isAr ? 'صفقة مغلقة' : 'Closed Deals'}
          value={totalKpis.closed}
          sub={`${fmt(totalKpis.sarClosed)} SAR`}
          accent="#22c55e"
        />
        <Kpi
          label={isAr ? 'متوسط العمر (يوم)' : 'Avg Age (days)'}
          value={totalKpis.avgAge}
          sub={`${totalKpis.pct}% ${isAr ? 'نسبة الإغلاق' : 'closure rate'}`}
          accent="#6366f1"
        />
      </div>

      {/* ── Global controls (filters + search + sort) ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 8,
          padding: 10,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          marginBottom: 12,
        }}
      >
        <SegGroup
          options={[
            { v: 'inflight', label: `${isAr ? 'قيد التنفيذ' : 'In Progress'} (${totalKpis.inflight})` },
            { v: 'closed', label: `${isAr ? 'مغلقة' : 'Closed'} (${totalKpis.closed})` },
            { v: 'all', label: `${isAr ? 'الكل' : 'All'} (${totalKpis.total})` },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <SegGroup
          options={[
            { v: 'all', label: isAr ? 'الكل' : 'All' },
            { v: 'كاش', label: isAr ? 'كاش' : 'Cash' },
            { v: 'بنك', label: isAr ? 'بنك' : 'Bank' },
          ]}
          value={paymentFilter}
          onChange={setPaymentFilter}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isAr ? 'بحث: وحدة / عميل / منفّذ' : 'Search: unit / client / closer'}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '0.82rem',
            outline: 'none',
            minWidth: 0,
          }}
        />
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '0.82rem',
          }}
        >
          <option value="blocker">{isAr ? 'الأكثر تعثراً' : 'Most stuck first'}</option>
          <option value="value">{isAr ? 'الأعلى قيمة' : 'Highest value'}</option>
          <option value="name">{isAr ? 'حسب المشروع' : 'By project'}</option>
        </select>
      </div>

      {/* ── Active stuck-step filter chip ── */}
      {stepFilter && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.30)',
            borderRadius: 8,
            marginBottom: 10,
            fontSize: '0.78rem',
          }}
        >
          <span style={{ color: '#f59e0b', fontWeight: 800 }}>
            {isAr ? 'تصفية حسب الخطوة:' : 'Filtered by step:'}
          </span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
            {stagesForPayment(stepFilter.payment)[stepFilter.idx][lang]}
            <span style={{ color: 'var(--text-secondary)', marginInlineStart: 6, fontWeight: 600 }}>
              ({stepFilter.payment})
            </span>
          </span>
          <button
            onClick={clearStepFilter}
            style={{
              marginInlineStart: 'auto',
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontSize: '0.72rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {isAr ? 'مسح' : 'Clear'}
          </button>
        </div>
      )}

      {/* ── Bottleneck strip (compact bar chart, click to filter) ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <BottleneckPanel
          title={isAr ? 'مسار الشراء كاش' : 'Cash Pipeline'}
          rows={bottleneck.cash}
          payment="كاش"
          active={stepFilter}
          onPick={(idx) => setStepFilter({ payment: 'كاش', idx })}
          lang={lang}
        />
        <BottleneckPanel
          title={isAr ? 'مسار الشراء بنك' : 'Bank Pipeline'}
          rows={bottleneck.bank}
          payment="بنك"
          active={stepFilter}
          onPick={(idx) => setStepFilter({ payment: 'بنك', idx })}
          lang={lang}
        />
      </div>

      {/* ── Project group cards ── */}
      {groups.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            color: 'var(--text-secondary)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
          }}
        >
          {isAr ? 'لا توجد صفقات تطابق الفلاتر' : 'No deals match the current filters'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.map((g) => (
            <ProjectCard
              key={g.project}
              group={g}
              expanded={!!expanded[g.project]}
              onToggle={() => toggleExpand(g.project)}
              onPickStep={(payment, idx) => setStepFilter({ payment, idx })}
              onEditDeal={onEditDeal}
              onMarkSold={onMarkSold}
              lang={lang}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Project card: summary header + (when expanded) inline unit table ──
function ProjectCard({ group, expanded, onToggle, onPickStep, onEditDeal, onMarkSold, lang, t }) {
  const isAr = lang === 'ar';
  // Fully-closed projects collapse to a single-line summary by default.
  if (group.allClosed && !expanded) {
    return (
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderInlineStart: '3px solid #22c55e',
          borderRadius: 8,
          cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-primary)' }}>
          {projLabel(group.project, t)}
        </span>
        <span
          style={{
            fontSize: '0.7rem',
            fontWeight: 800,
            padding: '2px 8px',
            borderRadius: 10,
            background: '#22c55e22',
            color: '#22c55e',
          }}
        >
          {'\u2713 '}{isAr ? `${group.closed.length} مغلقة` : `${group.closed.length} closed`}
        </span>
        <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', direction: 'ltr' }}>
          {fmt(group.sarClosed)} SAR
        </span>
        <span style={{ marginInlineStart: 'auto', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
          {isAr ? 'عرض' : 'View'} ▸
        </span>
      </div>
    );
  }

  const blockerColor = group.avgAge >= 21 ? '#ef4444' : group.avgAge >= 7 ? '#f59e0b' : '#22c55e';

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderInlineStart: `3px solid ${group.inflight.length > 0 ? '#f59e0b' : '#22c55e'}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Header — always visible, click to toggle */}
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 14,
          padding: '12px 14px',
          cursor: 'pointer',
          alignItems: 'center',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>
              {projLabel(group.project, t)}
            </span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
              {group.inflight.length} {isAr ? 'قيد التنفيذ' : 'in-flight'}
              {group.closed.length > 0 && (
                <> · {group.closed.length} {isAr ? 'مغلقة' : 'closed'}</>
              )}
              {' · '}
              <span style={{ direction: 'ltr' }}>{group.cashN}</span> {isAr ? 'كاش' : 'cash'} ·{' '}
              <span style={{ direction: 'ltr' }}>{group.bankN}</span> {isAr ? 'بنك' : 'bank'}
            </span>
          </div>
          {/* Progress strip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <div style={{ flex: 1, height: 6, background: 'var(--bg-primary)', borderRadius: 3, overflow: 'hidden', minWidth: 80 }}>
              <div style={{ width: `${group.closureRate}%`, height: '100%', background: '#22c55e', transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-secondary)', direction: 'ltr', minWidth: 40, textAlign: 'end' }}>
              {group.closureRate}%
            </span>
          </div>
          {/* Inline blocker + age + value summary */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: '0.74rem' }}>
            {group.topBlocker && (
              <button
                onClick={(e) => { e.stopPropagation(); onPickStep(group.topBlocker.payment, group.topBlocker.idx); }}
                style={{
                  background: 'rgba(245,158,11,0.10)',
                  border: '1px solid rgba(245,158,11,0.30)',
                  borderRadius: 6,
                  padding: '4px 10px',
                  cursor: 'pointer',
                  color: '#f59e0b',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                }}
                title={isAr ? 'تصفية الجدول حسب هذه الخطوة' : 'Filter to units stuck on this step'}
              >
                {isAr ? 'أكبر عائق:' : 'Top blocker:'}{' '}
                <span style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                  {group.topBlocker.idx + 1}. {group.topBlocker.label}
                </span>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>
                  ({group.topBlocker.count} {isAr ? 'وحدة' : 'units'})
                </span>
              </button>
            )}
            {group.inflight.length > 0 && (
              <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>
                {isAr ? 'متوسط العمر:' : 'Avg age:'}{' '}
                <span style={{ color: blockerColor, fontWeight: 800, direction: 'ltr' }}>
                  {group.avgAge} {isAr ? 'يوم' : 'd'}
                </span>
                {group.oldestUnit && (
                  <>
                    {' · '}
                    {isAr ? 'الأقدم:' : 'Oldest:'}{' '}
                    <span style={{ color: 'var(--text-primary)', fontWeight: 800, direction: 'ltr' }}>
                      {group.oldestUnit.unit_code} ({group.oldestUnit._ageDays}d)
                    </span>
                  </>
                )}
              </span>
            )}
            <span style={{ color: 'var(--text-secondary)', fontWeight: 700, direction: 'ltr', marginInlineStart: 'auto' }}>
              {fmt(group.sarInflight)} SAR {isAr ? 'متعثرة' : 'blocked'}
            </span>
          </div>
        </div>
        <div
          style={{
            fontSize: '0.85rem',
            color: 'var(--text-secondary)',
            fontWeight: 800,
            padding: '4px 10px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            whiteSpace: 'nowrap',
          }}
        >
          {expanded ? (isAr ? '▾ إخفاء' : '▾ Hide') : (isAr ? '▸ عرض الوحدات' : `▸ View ${group.list.length}`)}
        </div>
      </div>

      {/* Expanded — inline unit table */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-primary)', textAlign: isAr ? 'right' : 'left' }}>
                <Th>{isAr ? 'الوحدة' : 'Unit'}</Th>
                <Th>{isAr ? 'العميل' : 'Client'}</Th>
                <Th>{isAr ? 'المنفّذ' : 'Closer'}</Th>
                <Th>{isAr ? 'الطريقة' : 'Method'}</Th>
                <Th>{isAr ? 'متعثر عند' : 'Stuck on'}</Th>
                <Th>{isAr ? 'العمر' : 'Age'}</Th>
                <Th style={{ textAlign: isAr ? 'left' : 'right' }}>{isAr ? 'القيمة' : 'Value'}</Th>
                <Th>{isAr ? 'التقدم' : 'Progress'}</Th>
                <Th style={{ textAlign: 'center' }}></Th>
              </tr>
            </thead>
            <tbody>
              {group.list.map((u) => (
                <UnitRow
                  key={u.id}
                  u={u}
                  onEditDeal={onEditDeal}
                  onMarkSold={onMarkSold}
                  lang={lang}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UnitRow({ u, onEditDeal, onMarkSold, lang }) {
  const isAr = lang === 'ar';
  const ageColor = u._closed ? '#22c55e' : u._ageDays >= 21 ? '#ef4444' : u._ageDays >= 7 ? '#f59e0b' : '#22c55e';
  const methodChip = u.payment_method === 'بنك'
    ? { bg: '#3b82f618', fg: '#3b82f6', label: isAr ? 'بنك' : 'Bank' }
    : { bg: '#22c55e18', fg: '#22c55e', label: isAr ? 'كاش' : 'Cash' };
  return (
    <tr style={{ borderTop: '1px solid var(--border)' }}>
      <Td style={{ fontWeight: 800, direction: 'ltr' }}>{u.unit_code}</Td>
      <Td>{u.client_name || '—'}</Td>
      <Td style={{ color: 'var(--accent)', fontWeight: 700 }}>{u.closer || '—'}</Td>
      <Td>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 10,
            background: methodChip.bg,
            color: methodChip.fg,
            fontSize: '0.7rem',
            fontWeight: 800,
            whiteSpace: 'nowrap',
          }}
        >
          {methodChip.label}
        </span>
      </Td>
      <Td>
        {u._closed ? (
          <span style={{ color: '#22c55e', fontWeight: 800, fontSize: '0.78rem' }}>
            {'\u2713 '}{isAr ? 'مكتملة' : 'Complete'}
          </span>
        ) : u._stuckIdx >= 0 ? (
          <span style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '0.78rem' }}>
            {u._stuckIdx + 1}. {u._stuckLabel}
          </span>
        ) : (
          <span style={{ color: 'var(--text-secondary)' }}>—</span>
        )}
      </Td>
      <Td>
        <span
          style={{
            fontWeight: 800,
            color: ageColor,
            direction: 'ltr',
            fontSize: '0.78rem',
          }}
        >
          {u._closed ? '—' : `${u._ageDays}d`}
        </span>
      </Td>
      <Td style={{ direction: 'ltr', textAlign: isAr ? 'left' : 'right', fontWeight: 800, color: '#c9a84c' }}>
        {fmt(u._value)}
      </Td>
      <Td>
        <ProgressBar done={u._done} total={u._stages.length} closed={u._closed} />
      </Td>
      <Td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
          {/* Closed-but-still-Reserved → offer a one-click flip to Sold (مباع).
              The closure checklist and the unit's inventory status are otherwise
              unlinked, so a finished deal sits on محجوز until flipped here. */}
          {onMarkSold && u._closed && u.status === 'محجوز' && (
            <button
              onClick={() => onMarkSold(u)}
              title={isAr ? 'تحويل الوحدة من محجوز إلى مباع' : 'Flip this unit from Reserved to Sold'}
              style={{
                padding: '6px 12px',
                fontSize: '0.76rem',
                fontWeight: 800,
                background: 'rgba(34,197,94,0.12)',
                border: '1.5px solid #22c55e',
                color: '#16a34a',
                borderRadius: 6,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {isAr ? '✓ تحديد كمباع' : '✓ Mark Sold'}
            </button>
          )}
          {onEditDeal && u._deal && (
            <button
              onClick={() => onEditDeal(u._deal, u)}
              style={{
                padding: '6px 12px',
                fontSize: '0.76rem',
                fontWeight: 800,
                background: 'rgba(99,102,241,0.12)',
                border: '1.5px solid var(--accent)',
                color: 'var(--accent)',
                borderRadius: 6,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {isAr ? 'فتح' : 'Open'}
            </button>
          )}
        </div>
      </Td>
    </tr>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderInlineStart: `3px solid ${accent}`,
      borderRadius: 8,
      padding: '10px 14px',
    }}>
      <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', fontWeight: 700, marginBottom: 4, letterSpacing: 0.3, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: accent, lineHeight: 1.1 }}>{value}</div>
      {sub && (
        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 700, marginTop: 2, direction: 'ltr' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// Click any step to filter the project cards to units stuck on that step.
function BottleneckPanel({ title, rows, payment, active, onPick, lang }) {
  const maxCount = Math.max(0, ...rows.map((r) => r.count));
  const total = rows.reduce((a, r) => a + r.count, 0);
  const isAr = lang === 'ar';
  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      background: 'var(--bg-card)',
      padding: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <div style={{
          fontSize: '0.72rem',
          fontWeight: 800,
          color: 'var(--text-secondary)',
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}>
          {title}
        </div>
        <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', fontWeight: 700 }}>
          {total} {isAr ? 'إجابة معلقة' : 'pending answers'}
        </span>
      </div>
      {rows.map((r) => {
        const isPeak = r.count === maxCount && maxCount > 0;
        const isActive = active && active.payment === payment && active.idx === r.idx;
        const pct = maxCount > 0 ? Math.round((r.count / maxCount) * 100) : 0;
        return (
          <button
            key={r.idx}
            onClick={() => onPick(r.idx)}
            disabled={r.count === 0}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 80px 32px',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '5px 6px',
              borderRadius: 5,
              background: isActive ? 'rgba(99,102,241,0.14)' : isPeak ? 'rgba(245,158,11,0.10)' : 'transparent',
              border: isActive ? '1px solid var(--accent)' : '1px solid transparent',
              fontSize: '0.78rem',
              cursor: r.count > 0 ? 'pointer' : 'default',
              opacity: r.count === 0 ? 0.5 : 1,
              textAlign: isAr ? 'right' : 'left',
              color: 'inherit',
              marginBottom: 2,
            }}
          >
            <span style={{
              color: 'var(--text-primary)',
              fontWeight: isPeak || isActive ? 700 : 500,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {r.idx + 1}. {r.label}
              {r.optional && (
                <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', marginInlineStart: 4 }}>
                  {isAr ? '(اختياري)' : '(opt)'}
                </span>
              )}
            </span>
            <span style={{ height: 5, background: 'var(--bg-primary)', borderRadius: 3, overflow: 'hidden' }}>
              <span style={{
                display: 'block',
                width: `${pct}%`,
                height: '100%',
                background: isActive ? 'var(--accent)' : isPeak ? '#f59e0b' : '#94a3b8',
              }} />
            </span>
            <span style={{
              fontWeight: 800,
              color: isActive ? 'var(--accent)' : isPeak ? '#f59e0b' : 'var(--text-secondary)',
              textAlign: 'end',
              direction: 'ltr',
            }}>
              {r.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ProgressBar({ done, total, closed }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = closed ? '#22c55e' : '#f59e0b';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
      <div style={{
        flex: 1,
        height: 6,
        background: 'var(--bg-primary)',
        borderRadius: 3,
        overflow: 'hidden',
        minWidth: 50,
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 700, whiteSpace: 'nowrap', direction: 'ltr' }}>
        {done}/{total}
      </span>
    </div>
  );
}

// Segmented toggle: visually unifies the parallel status / payment chips.
function SegGroup({ options, value, onChange }) {
  return (
    <div
      role="group"
      style={{
        display: 'flex',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 2,
        gap: 2,
        minWidth: 0,
      }}
    >
      {options.map((o) => {
        const active = o.v === value;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              flex: 1,
              padding: '7px 10px',
              borderRadius: 4,
              border: 'none',
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? '#fff' : 'var(--text-secondary)',
              fontSize: '0.76rem',
              fontWeight: 800,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Th({ children, style }) {
  return (
    <th style={{
      padding: '8px 10px',
      fontSize: '0.7rem',
      fontWeight: 700,
      color: 'var(--text-secondary)',
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      ...style,
    }}>
      {children}
    </th>
  );
}

function Td({ children, style }) {
  return (
    <td style={{ padding: '8px 10px', ...style }}>
      {children}
    </td>
  );
}
